import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthService } from '../services/authService';

interface User {
  userId: string;
  username: string;
  attributes?: {
    email?: string;
    name?: string;
    [key: string]: any;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string; needsConfirmation?: boolean }>;
  confirmSignUp: (username: string, code: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const refreshAuth = async () => {
    try {
      const sessionResult = await AuthService.getAuthSession();

      if (sessionResult.success && sessionResult.isAuthenticated) {
        const userResult = await AuthService.getCurrentUser();

        if (userResult.success && userResult.user) {
          setUser({
            userId: userResult.user.userId,
            username: userResult.user.username,
            attributes: {
              email: userResult.user.signInDetails?.loginId || userResult.user.username,
            },
          });
          setIsAuthenticated(true);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error refreshing auth:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const result = await AuthService.signIn({ email, password });

      if (result.success) {
        await refreshAuth();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Sign in failed'
      };
    }
  };

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      const result = await AuthService.signUp({ email, password, name });

      if (result.success) {
        // Store the username for confirmation since that's what Cognito needs
        if (!result.isSignUpComplete && result.username) {
          sessionStorage.setItem('pendingSignUpUsername', result.username);
          sessionStorage.setItem('pendingSignUpEmail', email);
        }
        return {
          success: true,
          needsConfirmation: !result.isSignUpComplete
        };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Sign up failed'
      };
    }
  };

  const confirmSignUp = async (username: string, code: string) => {
    try {
      const result = await AuthService.confirmSignUp({ username, confirmationCode: code });

      if (result.success && result.isSignUpComplete) {
        // Clear stored signup data
        sessionStorage.removeItem('pendingSignUpUsername');
        sessionStorage.removeItem('pendingSignUpEmail');
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Confirmation failed' };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Confirmation failed'
      };
    }
  };

  const signOut = async () => {
    try {
      await AuthService.signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};